import { useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { FormActions, Toolbar } from "@jbnl/shared-ui";
import { ListDetail } from "@/components/ListDetail";
import { RowListSkeleton } from "@/components/RowListSkeleton";
import { VolgordeKnoppen } from "@/components/VolgordeKnoppen";
import { verplaats } from "@/lib/volgorde";
import { BezettingGrid } from "./BezettingGrid";
import { FunctieForm } from "./FunctieForm";
import {
  NOODZAAK,
  scopeLabel,
  useCreateFunctie,
  useDeleteFunctie,
  useFuncties,
  useReorderFuncties,
  useUpdateFunctie,
  type Functie,
} from "./useStamgegevens";

/**
 * One list serves all klassen: a klasse needing its own standard gets a separately named functie,
 * which is why Brandbestrijding has its own Teamleider rather than a flag.
 */
export function Functies({
  functieId,
  onKies,
}: {
  functieId: string | undefined;
  onKies: (id?: string) => void;
}) {
  const { t } = useTranslation("stamgegevens");
  const { data: functies, isPending } = useFuncties();
  const functie = functies?.find((f) => f.id === functieId);

  return (
    <>
      <Toolbar>
        <NieuweFunctie onAangemaakt={onKies} />
      </Toolbar>

      <ListDetail
        selected={Boolean(functie)}
        backLabel={t("functies.terug")}
        onBack={() => onKies()}
        list={
          <Card>
            <CardContent className="pt-6">
              {isPending ? (
                <RowListSkeleton
                  rows={6}
                  subtitle
                  actions={["size-8", "size-8"]}
                  rowClassName="py-1"
                  labelClassName="px-2 py-1.5"
                />
              ) : (
                <FunctieLijst
                  functies={functies ?? []}
                  gekozenId={functie?.id}
                  onKies={onKies}
                />
              )}
            </CardContent>
          </Card>
        }
        detail={
          functie ? (
            <FunctieDetail
              key={functie.id}
              functie={functie}
              onVerwijderd={() => onKies()}
            />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  {t("functies.kies")}
                </p>
              </CardContent>
            </Card>
          )
        }
      />
    </>
  );
}

/** A plain list, not a `DataTable`: sortable headers and a hand-held order fight over one list. */
function FunctieLijst({
  functies,
  gekozenId,
  onKies,
}: {
  functies: Functie[];
  gekozenId: string | undefined;
  onKies: (id: string) => void;
}) {
  const { t } = useTranslation("stamgegevens");
  const reorder = useReorderFuncties();

  function verzet(index: number, delta: number) {
    reorder.mutate(verplaats(functies, index, delta).map((f) => f.id));
  }

  if (functies.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">{t("functies.lijstLeeg")}</p>
    );
  }

  return (
    <>
      <ul className="divide-y">
        {functies.map((functie, index) => (
          <li key={functie.id} className="flex items-center gap-2 py-1">
            <button
              type="button"
              onClick={() => onKies(functie.id)}
              aria-current={functie.id === gekozenId ? "true" : undefined}
              className="flex flex-1 flex-col items-start rounded-md px-2 py-1.5 text-left hover:bg-muted aria-[current]:bg-secondary-surface aria-[current]:font-bold aria-[current]:text-secondary-active"
            >
              <span>{functie.name}</span>
              <span className="text-xs font-normal text-muted-foreground">
                {functie.noodzaak === NOODZAAK.optioneel
                  ? t("functies.scopeMetNoodzaak", {
                      scope: scopeLabel(functie.scope),
                      noodzaak: t("noodzaak.optioneel"),
                    })
                  : scopeLabel(functie.scope)}
                {functie.weergavenaam &&
                  t("functies.weergavenaamInLijst", {
                    weergavenaam: functie.weergavenaam,
                  })}
              </span>
            </button>
            <VolgordeKnoppen
              label={functie.name}
              eerste={index === 0}
              laatste={index === functies.length - 1}
              disabled={reorder.isPending}
              onOmhoog={() => verzet(index, -1)}
              onOmlaag={() => verzet(index, 1)}
            />
          </li>
        ))}
      </ul>
      {reorder.error && (
        <p className="mt-2 text-sm text-destructive">{reorder.error.message}</p>
      )}
    </>
  );
}

function NieuweFunctie({
  onAangemaakt,
}: {
  onAangemaakt: (id: string) => void;
}) {
  const { t } = useTranslation("stamgegevens");
  const create = useCreateFunctie();
  const [open, setOpen] = useState(false);

  function wissel(volgende: boolean) {
    setOpen(volgende);
    if (!volgende) create.reset();
  }

  return (
    <Dialog open={open} onOpenChange={wissel}>
      <DialogTrigger asChild>
        <Button>
          <Plus />
          {t("functies.toevoegen")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("functies.toevoegen")}</DialogTitle>
          <DialogDescription>{t("functies.toevoegenUitleg")}</DialogDescription>
        </DialogHeader>

        <FunctieForm
          key={String(open)}
          id="nieuwe-functie"
          submitLabel={t("functies.submitToevoegen")}
          pending={create.isPending}
          error={create.error}
          onSubmit={(waarden) =>
            create.mutate(
              {
                name: waarden.name,
                scope: waarden.scope,
                noodzaak: waarden.noodzaak,
                weergavenaam: waarden.weergavenaam,
              },
              {
                onSuccess: (nieuw) => {
                  wissel(false);
                  onAangemaakt(nieuw.id);
                },
              },
            )
          }
        />
      </DialogContent>
    </Dialog>
  );
}

function FunctieDetail({
  functie,
  onVerwijderd,
}: {
  functie: Functie;
  onVerwijderd: () => void;
}) {
  const { t } = useTranslation(["stamgegevens", "common"]);
  const update = useUpdateFunctie();
  const remove = useDeleteFunctie();

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{functie.name}</CardTitle>
        </CardHeader>
        <CardContent>
          <FunctieForm
            id="functie"
            functie={functie}
            submitLabel={t("common:actions.save")}
            pending={update.isPending}
            error={update.error}
            scopeVast={functie.inUse}
            onSubmit={(waarden) =>
              update.mutate({ id: functie.id, ...waarden })
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("functies.bezettingTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          <BezettingGrid functie={functie} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("functies.verwijderenTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t("functies.verwijderenUitleg")}
          </p>
          {/* A delete card standing alone takes the corner: it has no Save to be mistaken for. */}
          <FormActions>
            {remove.error && (
              <p className="text-sm text-destructive">{remove.error.message}</p>
            )}
            <ConfirmDelete
              trigger={
                <Button variant="destructive" disabled={remove.isPending}>
                  {remove.isPending
                    ? t("common:actions.busy")
                    : t("common:actions.delete")}
                </Button>
              }
              title={t("functies.verwijderenVraag", { naam: functie.name })}
              description={t("functies.verwijderenDescription")}
              confirmLabel={t("functies.verwijderenConfirm", {
                naam: functie.name,
              })}
              onConfirm={() =>
                remove.mutate(functie.id, { onSuccess: onVerwijderd })
              }
            />
          </FormActions>
        </CardContent>
      </Card>
    </div>
  );
}
